/**
 * Bulk student upload — Excel template download + file upload + result/errors
 */

export default function StudentBulkUpload({
  open,
  onToggle,
  onDownloadTemplate,
  downloadDisabled,
  uploadFile,
  onFileChange,
  onSubmit,
  uploadSubmitting,
  uploadResult,
  uploadError,
  inputId = 'student-master-upload-input',
}) {
  if (!open) return null;

  const created = uploadResult?.created?.length ?? 0;
  const failed = uploadResult?.failed ?? 0;
  const rowErrors = Array.isArray(uploadResult?.errors) ? uploadResult.errors : [];
  const allOk = uploadResult && failed === 0 && created > 0;
  const partial = uploadResult && created > 0 && failed > 0;

  return (
    <div className="student-upload-panel" role="region" aria-label="Upload students from Excel">
      <p className="student-upload-intro">
        Download the template, fill in student rows, then upload. Default password for new accounts:{' '}
        <strong>Student@123</strong>
      </p>
      <div className="student-upload-actions">
        <button
          type="button"
          onClick={onDownloadTemplate}
          disabled={downloadDisabled}
          className="btn-secondary student-upload-btn"
        >
          Download template (.xlsx)
        </button>
        <form onSubmit={onSubmit} className="student-upload-form">
          <input
            id={inputId}
            type="file"
            accept=".xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
            onChange={(e) => onFileChange(e.target.files?.[0] || null)}
            className="student-upload-file"
            aria-label="Choose Excel or CSV file"
          />
          <button
            type="submit"
            disabled={!uploadFile || uploadSubmitting || downloadDisabled}
            className="btn-primary student-upload-btn"
          >
            {uploadSubmitting ? 'Uploading…' : 'Upload file'}
          </button>
        </form>
        {onToggle ? (
          <button type="button" className="btn-secondary student-upload-btn" onClick={onToggle}>
            Close
          </button>
        ) : null}
      </div>

      {uploadError ? (
        <div className="student-upload-alert student-upload-alert--error" role="alert">
          <p className="student-upload-alert-title">Upload failed</p>
          <p className="student-upload-alert-text">{uploadError}</p>
        </div>
      ) : null}

      {uploadResult ? (
        <div
          className={`student-upload-alert ${allOk ? 'student-upload-alert--success' : partial ? 'student-upload-alert--warn' : 'student-upload-alert--error'}`}
          role="status"
        >
          <p className="student-upload-alert-title">
            {allOk
              ? `Successfully added ${created} student${created === 1 ? '' : 's'}.`
              : partial
                ? `Added ${created} student${created === 1 ? '' : 's'}; ${failed} row${failed === 1 ? '' : 's'} failed.`
                : created === 0
                  ? 'No students were added.'
                  : `Upload finished: ${created} created, ${failed} failed.`}
          </p>
          <p className="student-upload-alert-text">
            Use the filters above and click <strong>Show</strong> to view the updated roster.
          </p>
          {rowErrors.length > 0 ? (
            <ul className="student-upload-error-list">
              {rowErrors.slice(0, 25).map((err, idx) => (
                <li key={`${err.row}-${err.email}-${idx}`}>
                  <strong>Row {err.row}</strong>
                  {err.email ? ` (${err.email})` : ''}: {err.message}
                </li>
              ))}
              {rowErrors.length > 25 ? (
                <li>… and {rowErrors.length - 25} more error(s)</li>
              ) : null}
            </ul>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
